import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, EMAIL_RE, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AdminLayout, btnPrimary, inputCls, Panel } from "@/components/admin/AdminLayout";

const Msg = ({ m, testId }) =>
  m ? <p className={`text-sm font-medium ${m.ok ? "text-[#7ee08a]" : "text-[#ff6b5e]"}`} data-testid={testId} role="status">{m.text}</p> : null;

const PwField = ({ label, value, onChange, testId, type = "password", auto }) => (
  <div>
    <Label>{label}</Label>
    <Input type={type} value={value} autoComplete={auto} onChange={(e) => onChange(e.target.value)} className={`${inputCls} mt-1.5`} data-testid={testId} />
  </div>
);

const useSubmit = () => {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const run = async (fn) => {
    setBusy(true);
    setMsg(null);
    try {
      setMsg({ ok: true, text: await fn() });
      return true;
    } catch (e) {
      setMsg({ ok: false, text: errMsg(e) });
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, msg, setMsg, run };
};

export default function AccountPage() {
  const { user, updateSession } = useAuth();
  const [pw, setPw] = useState({ current: "", next: "" });
  const [em, setEm] = useState({ email: "", current: "" });
  const pwSub = useSubmit();
  const emSub = useSubmit();

  const changePw = async (e) => {
    e.preventDefault();
    if (pw.next.length < 8) return pwSub.setMsg({ ok: false, text: "Password must be at least 8 characters" });
    const ok = await pwSub.run(async () => (await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next })).data.message);
    if (ok) setPw({ current: "", next: "" });
  };
  const changeEmail = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(em.email.trim())) return emSub.setMsg({ ok: false, text: "Enter a valid email" });
    const ok = await emSub.run(async () => {
      const { data } = await api.post("/auth/change-email", { new_email: em.email, current_password: em.current });
      updateSession(data.token, data.user);
      return data.message;
    });
    if (ok) setEm({ email: "", current: "" });
  };

  return (
    <AdminLayout title="Account" testId="admin-account-page">
      <p className="mb-6 text-zinc-400">Logged in as <span className="text-zinc-100" data-testid="account-current-email">{user?.email}</span></p>
      <Panel title="Change password" testId="change-password-panel">
        <form onSubmit={changePw} noValidate className="space-y-4">
          <PwField label="Current password" value={pw.current} onChange={(v) => setPw({ ...pw, current: v })} testId="current-password-input" auto="current-password" />
          <PwField label="New password (at least 8 characters)" value={pw.next} onChange={(v) => setPw({ ...pw, next: v })} testId="new-password-input" auto="new-password" />
          <Msg m={pwSub.msg} testId="change-password-message" />
          <Button type="submit" disabled={pwSub.busy} className={btnPrimary} data-testid="change-password-submit-button">
            {pwSub.busy && <Loader2 className="h-4 w-4 animate-spin" />} Update password
          </Button>
        </form>
      </Panel>
      <Panel title="Change login email" testId="change-email-panel">
        <form onSubmit={changeEmail} noValidate className="space-y-4">
          <PwField label="New login email" type="email" value={em.email} onChange={(v) => setEm({ ...em, email: v })} testId="new-email-input" auto="email" />
          <PwField label="Current password" value={em.current} onChange={(v) => setEm({ ...em, current: v })} testId="email-current-password-input" auto="current-password" />
          <Msg m={emSub.msg} testId="change-email-message" />
          <Button type="submit" disabled={emSub.busy} className={btnPrimary} data-testid="change-email-submit-button">
            {emSub.busy && <Loader2 className="h-4 w-4 animate-spin" />} Update login email
          </Button>
        </form>
      </Panel>
    </AdminLayout>
  );
}
